import { Box, Typography, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EcoOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import { FiPlus, FiRefreshCw, FiCopy, FiShare2, FiTrash2 } from "react-icons/fi";
import { LuFilter, LuMapPin } from "react-icons/lu";
import { RiDownload2Line } from "react-icons/ri";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import m3Styles from "../../../components/m3/m3.module.css";
import { useAuth } from "../../../authentication/AuthProvider";
import SaveButton from "../../../components/button/SaveButton";
import { MainContainer } from "../../../components/container/MainContainer";
import ColumnMenu from "../../../components/context-menu/ColumnMenu";
import ContextMenu from "../../../components/context-menu/ContextMenu";
import DialogDelete from "../../../components/dialog/DialogDelete";
import AgGrid from "../../../components/grid/AgGrid";
import { centerAlignColDef, checkBoxColDef, defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef, tonsFormatter } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { ToggleAll } from "../../../components/grid/headers/ToggleAll";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Structure } from "../../../db_access/structure";
import { StructureTemplateSharing } from "../../../db_access/structure_template_sharing";
import { IndustryTypes, StructureTypes } from "./StructureType";
import OverallPerformanceAnalytics from "../OverallAnalytics";
import { ORG_PALETTE } from "../../../theme/style/organizationPalette";
import {
  KpiGrid,
  KpiTile,
  DataTableCard,
  M3Button,
  M3IconButton,
  M3Badge,
  StatusChip,
  GlobalViewToggle,
  GlobalViewSection,
  M3GridSelectEditor,
  M3Dialog,
  M3Field,
} from "../../../components/m3";

const defaultNewStructure = {
  name: "",
  type: "",
  location: "",
  year: new Date().getFullYear(),
  cost: 0,
  gwp: 0,
  is_published_template: false,
  validation_status: "Not Validated"
};

const StructureDashboard = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const {
    columnVisibilities,
    refresh,
    setRefresh,
    setSelectedStructures,
    setIsLoading,
    isSaveClicked,
    setIsSaveVisible,
    setIsSaveClicked,
    enqueueAlert,
  } = useContextProvider();
  const { t } = useTranslation();
  const { isAdmin, companyID } = useAuth();
  const gridHeaderContainerRef = useRef(null);
  const dialogRef = useRef(null);
  const gridRef = useRef(null);
  const isSavingRef = useRef(false);
  const pendingFocusIdRef = useRef(null);
  const scopeDataRef = useRef([]);

  const [structures, setStructures] = useState([]);
  const [actionQueue, setActionQueue] = useState([]);
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [globalView, setGlobalView] = useState(false);
  const [scopeData, setScopeData] = useState([]);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);
  const [customIndustryDialogOpen, setCustomIndustryDialogOpen] = useState(false);
  const [customIndustryRowId, setCustomIndustryRowId] = useState(null);
  const [customIndustryValue, setCustomIndustryValue] = useState("");

  useEffect(() => {
    scopeDataRef.current = scopeData;
  }, [scopeData]);

  const colVisibility_key = "structure_dashboard_table_column_visibility";
  const defaultCols = ["name", "type", "location", "year", "cost", "gwp", "validation_status"];

  // Derive selected items from local IDs and current structures
  const derivedSelectedStructures = useMemo(() => {
    if (localSelectedIds.length === 0) return [];
    return structures.filter((s) => localSelectedIds.includes(s.id));
  }, [structures, localSelectedIds]);

  const onSelectionChanged = useCallback((event) => {
    const selected = event.api.getSelectedRows();
    const newIds = selected.map((s) => s.id);
    setLocalSelectedIds(newIds);
    setSelectedStructures(selected.map((s) => ({ ...s })));
  }, [setSelectedStructures]);

  const newOrganization = () => {
    if (isAdding) return;
    setIsAdding(true);
    const newS = { ...defaultNewStructure, id: uuidv4() };
    console.log('[StructureDashboard] ➕ NEW organization triggered, id:', newS.id);
    pendingFocusIdRef.current = newS.id;
    setStructures((p) => [...p, newS]);
    setSelectedStructures([newS]);
    setActionQueue((q) => [...q, { action: "new", item: newS }]);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Organization created successfully") });
  };

  const deleteStructures = (selectedItems) => {
    const ids = selectedItems.map((s) => s.id);
    console.log('[StructureDashboard] 🗑️ DELETE triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "delete", ids }]);
    setStructures((p) => p.filter((s) => !ids.includes(s.id)));
    setLocalSelectedIds([]);
    setSelectedStructures([]);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Organization deleted successfully") });
  };

  const archiveStructures = (selectedItems) => {
    const ids = selectedItems.map((s) => s.id);
    console.log('[StructureDashboard] 📦 ARCHIVE triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "archive", ids }]);
    setStructures((p) => p.filter((s) => !ids.includes(s.id)));
    setLocalSelectedIds([]);
    setSelectedStructures([]);
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Organization archived successfully") });
  };

  const publishStructures = (selectedItems) => {
    const ids = selectedItems.map((s) => s.id);
    console.log('[StructureDashboard] 📢 PUBLISH triggered, ids:', ids);
    setActionQueue((q) => [...q, { action: "publish", ids }]);
    setStructures((p) => p.map((s) => (ids.includes(s.id) ? { ...s, is_published_template: true } : s)));
    setIsSaveVisible(true);
    enqueueAlert({ message: t("Organization marked as published template") });
  };

  const duplicateStructures = async (selectedItems) => {
    const ids = selectedItems.map((s) => s.id);
    try {
      await Structure.duplicate(ids);
      enqueueAlert({ message: t("Organization duplicated successfully"), severity: "success" });
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("[StructureDashboard] ❌ Duplicate failed:", error);
      enqueueAlert({ message: t("Failed to duplicate organization"), severity: "error" });
    }
  };

  const save = useCallback(async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    try {
      console.log('[StructureDashboard] 🚀 save() called with', actionQueue.length, 'actions in queue');
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.stopEditing();
      }
      const id_map = {};
      const processedIds = new Set();
      for (const actionItem of actionQueue) {
        const { action, item, ids, checked } = actionItem;
        if (action === "new") {
          const latestItem = structures.find((s) => s.id === item.id) || item;
          const res = await Structure.new(latestItem);
          const data = await res.json();
          if (data?.id) {
            id_map[item.id] = data.id;
            processedIds.add(item.id);
            setStructures((prev) => prev.map((s) => (s.id === item.id ? { ...s, id: data.id } : s)));
            setSelectedStructures((prev) => prev.map((s) => (s.id === item.id ? { ...s, id: data.id } : s)));
          }
        } else if (action === "update") {
          if (processedIds.has(item.id)) continue;
          const updatedItem = { ...item, id: id_map[item.id] || item.id };
          await Structure.update([updatedItem]);
        } else if (action === "delete") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          await Structure.delete(mapped_ids);
        } else if (action === "archive") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          await Structure.archive(mapped_ids);
        } else if (action === "publish") {
          const mapped_ids = ids.map((id) => id_map[id] || id);
          const updatedStructures = structures.filter(s => mapped_ids.includes(s.id)).map(s => ({ ...s, id: id_map[s.id] || s.id, is_published_template: true }));
          const templateData = mapped_ids.map((id) => ({ structure_id: id_map[id] || id, company_id: companyID }));
          await Promise.all([
            Structure.update(updatedStructures),
            StructureTemplateSharing.new(templateData)
          ]);
        } else if (action === "update_published_templates") {
          const structuresToUpdate = structures.map((s) => ({ ...s, is_published_template: checked }));
          await Structure.update(structuresToUpdate);
        }
      }
      if (actionQueue.length === 0) return;

      setIsLoading(false);
      setIsSaveClicked(false);
      setIsSaveVisible(false);
      setActionQueue([]);
      setRefresh((r) => !r);
      enqueueAlert({ message: t("Changes saved successfully") });
    } catch (error) {
      console.error('[StructureDashboard] ❌ Save failed:', error);
      setIsLoading(false);
      setIsSaveClicked(false);
      enqueueAlert({ message: t("Failed to save changes"), severity: "error" });
    } finally {
      isSavingRef.current = false;
    }
  }, [actionQueue, structures, companyID, setIsLoading, setIsSaveClicked, setIsSaveVisible, setRefresh, enqueueAlert, t]);

  useEffect(() => {
    if (!isSaveClicked) return;
    save();
  }, [isSaveClicked, save]);

  // Aggregated Metrics
  const targetStructures = derivedSelectedStructures.length > 0 ? derivedSelectedStructures : structures;
  const activeIds = useMemo(() => targetStructures.map((s) => s.id), [targetStructures]);
  const activeScopeData = useMemo(() => scopeData.filter((d) => activeIds.includes(d.id)), [scopeData, activeIds]);

  const totalScope1 = useMemo(() => activeScopeData.reduce((acc, curr) => acc + (Number(curr.scope_1) || 0), 0), [activeScopeData]);
  const totalScope2 = useMemo(() => activeScopeData.reduce((acc, curr) => acc + (Number(curr.scope_2) || 0), 0), [activeScopeData]);
  const totalScope3 = useMemo(() => activeScopeData.reduce((acc, curr) => acc + (Number(curr.scope_3) || 0), 0), [activeScopeData]);
  const totalGWP = useMemo(() => totalScope1 + totalScope2 + totalScope3, [totalScope1, totalScope2, totalScope3]);

  const pctScope1 = totalGWP > 0 ? ((totalScope1 / totalGWP) * 100).toFixed(1) : "0";
  const pctScope2 = totalGWP > 0 ? ((totalScope2 / totalGWP) * 100).toFixed(1) : "0";
  const pctScope3 = totalGWP > 0 ? ((totalScope3 / totalGWP) * 100).toFixed(1) : "0";

  const totalAvoided = useMemo(() => activeScopeData.reduce((acc, curr) => acc + (Number(curr.avoided || curr.scope_avoided || curr.avoided_emissions) || 0), 0), [activeScopeData]);
  const totalBiogenic = useMemo(() => activeScopeData.reduce((acc, curr) => acc + (Number(curr.biogenic || curr.scope_biogenic || curr.biogenic_emissions) || 0), 0), [activeScopeData]);
  const totalRecords = useMemo(() => {
    const fromScope = activeScopeData.reduce((acc, curr) => acc + (Number(curr.records_count || curr.count || curr.emission_count) || 0), 0);
    return fromScope > 0 ? fromScope : activeIds.length;
  }, [activeScopeData, activeIds]);
  const recordsFooter = useMemo(() => {
    return t("{{count}} emission sources", { count: activeIds.length });
  }, [activeIds.length, t]);

  // Visual Cell Renderers
  const INDUSTRY_TONE_MAP = {
    "Transport, Courier & Logistics": "info",
    "Manufacturing & Industrial Production": "primary",
    "Chemicals, Plastics & Materials": "warning",
    "Wholesale & Distribution": "tertiary",
    "Construction & Built Environment": "neutral",
    "Professional, Design & Digital Services": "info",
    "Energy, Utilities & Resources": "warning",
    "Agriculture, Forestry & Food System": "success",
    "Retail, Hospitality & Consumer Services": "tertiary",
    "Healthcare, Education & Public Services": "primary",
    "Waste & Environmental Services": "success",
    "Arts, Media, Sport & Entertainment": "neutral",
    "Others (Editable)": "neutral",
  };

  const TypeCellRenderer = useCallback((params) => {
    if (!params.value) return "—";
    const tone = INDUSTRY_TONE_MAP[params.value] || "neutral";
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", width: "100%", height: "100%", overflow: "hidden" }}>
        <M3Badge tone={tone} size="sm" tooltip={params.value} style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {params.value}
        </M3Badge>
      </div>
    );
  }, []);

  const LocationCellRenderer = useCallback((params) => {
    if (!params.value) return "—";
    return (
      <div className={m3Styles.locationCell}>
        <span className={m3Styles.locationIcon}><LuMapPin size={14} /></span>
        <span>{params.value}</span>
      </div>
    );
  }, []);

  const GwpCellRenderer = useCallback((params) => {
    const formatted = params.valueFormatted || (Number(params.value) / 1000).toFixed(2);
    const numInKg = Number(params.value) || 0;
    const pct = totalGWP > 0 ? Math.min(100, Math.max(0, Math.round((numInKg / totalGWP) * 100))) : 0;

    return (
      <div className={m3Styles.gwpCell}>
        <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{formatted}</span>
        <div className={m3Styles.gwpBarTrack} title={`${pct}% of portfolio GWP`}>
          <div className={m3Styles.gwpBarFill} style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }, [totalGWP]);

  const ValidationStatusRenderer = useCallback((params) => {
    const status = params.value || "Not Validated";
    return (
      <div style={{ display: "inline-flex", justifyContent: "center", width: "100%" }}>
        <StatusChip status={status} />
      </div>
    );
  }, []);

  const columnDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);

    const onCellValueChanged = (params) => {
      const { data } = params;
      const now = new Date().toString().split(" GMT")[0];
      const updatedRow = { ...data, date_modified: now };
      console.log('[StructureDashboard] ✏️ CELL EDIT triggered, field:', params.colDef?.field, 'id:', updatedRow.id);

      if (params.colDef?.field === "type" && updatedRow.type === "Others (Editable)") {
        setCustomIndustryRowId(updatedRow.id);
        setCustomIndustryValue("");
        setCustomIndustryDialogOpen(true);
        return;
      }

      setStructures((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setSelectedStructures((prev) => prev.map((row) => (row.id === updatedRow.id ? { ...updatedRow } : row)));
      setActionQueue((q) => {
        const isNew = q.some((a) => a.action === "new" && a.item.id === updatedRow.id);
        if (isNew) {
          return q.map((a) => (a.action === "new" && a.item.id === updatedRow.id ? { ...a, item: updatedRow } : a));
        }
        return [...q, { action: "update", item: updatedRow }];
      });
      setIsSaveVisible(true);
    };

    const toggleAllPublishedTemplates = (checked) => {
      setStructures((rows) => rows.map((row) => ({ ...row, is_published_template: checked })));
      setActionQueue((q) => [...q, { action: "update_published_templates", checked }]);
      setIsSaveVisible(true);
    };

    const onCellClicked = (params) => {
      const { colDef } = params;
      const updatedRow = { ...params.data, [colDef.field]: !params.value };
      setStructures((prev) => prev.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
      setSelectedStructures((prev) => prev.map((row) => (row.id === updatedRow.id ? { ...updatedRow } : row)));
      setActionQueue((q) => {
        const isNew = q.some((a) => a.action === "new" && a.item.id === updatedRow.id);
        if (isNew) {
          return q.map((a) => (a.action === "new" && a.item.id === updatedRow.id ? { ...a, item: updatedRow } : a));
        }
        return [...q, { action: "update", item: updatedRow }];
      });
      setIsSaveVisible(true);
    };

    return [
      {
        headerName: "Name",
        field: "name",
        cellEditor: "agTextCellEditor",
        ...defaultColDef,
        ...leftAlignColDef,
        editable: true,
        onCellValueChanged,
        headerCheckboxSelection: true,
        checkboxSelection: false,
      },
      {
        headerName: "Industry Type",
        field: "type",
        cellEditor: M3GridSelectEditor,
        cellEditorParams: {
          values: IndustryTypes,
          tones: INDUSTRY_TONE_MAP,
        },
        cellRenderer: TypeCellRenderer,
        minWidth: 260,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: true,
        onCellValueChanged,
      },
      {
        headerName: "Location",
        field: "location",
        cellEditor: "agTextCellEditor",
        cellRenderer: LocationCellRenderer,
        ...defaultColDef,
        ...leftAlignColDef,
        editable: true,
        onCellValueChanged,
      },
      {
        headerName: "Year",
        field: "year",
        cellEditor: "agNumberCellEditor",
        ...defaultColDef,
        ...centerAlignColDef,
        editable: true,
        onCellValueChanged,
      },
      {
        headerName: "Cost",
        field: "cost",
        ...defaultColDef,
        ...rightAlignColDef,
        ...floatFormater,
        editable: true,
        onCellValueChanged,
      },
      {
        headerName: "GWP (tCO₂e)",
        field: "gwp",
        valueGetter: (params) => {
          if (!params.data) return 0;
          const matchingScope = scopeDataRef.current.find((d) => d.id === params.data.id);
          if (matchingScope) {
            return (Number(matchingScope.scope_1) || 0) + (Number(matchingScope.scope_2) || 0) + (Number(matchingScope.scope_3) || 0);
          }
          return params.data.gwp || 0;
        },
        cellRenderer: GwpCellRenderer,
        ...defaultColDef,
        ...rightAlignColDef,
        ...tonsFormatter,
      },
      ...(isAdmin
        ? [
          {
            headerName: "Publish Template",
            field: "is_published_template",
            ...checkBoxColDef,
            onCellClicked,
            headerComponent: ToggleAll,
            headerComponentParams: { toggle: toggleAllPublishedTemplates },
            editable: true,
          },
        ]
        : []),
      {
        headerName: "Validation Status",
        field: "validation_status",
        valueGetter: (params) => params.data?.validation_status || "Not Validated",
        cellRenderer: ValidationStatusRenderer,
        ...defaultColDef,
        ...centerAlignColDef,
      },
      {
        headerName: "Date Modified",
        field: "date_modified",
        ...defaultColDef,
        ...centerAlignColDef,
      },
    ].map((col) => ({
      ...col,
      hide: isHidden(col.field),
    }));
  }, [
    columnVisibilities,
    colVisibility_key,
    setIsSaveVisible,
    isAdmin,
    t,
    TypeCellRenderer,
    LocationCellRenderer,
    GwpCellRenderer,
    ValidationStatusRenderer,
  ]);

  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    const container = gridRef.current?.eGridDiv || gridHeaderContainerRef.current?.closest('.MuiPaper-root');
    const xPos = container ? container.getBoundingClientRect().left : e.clientX;
    setMenuPosition({ x: xPos, y: e.clientY });
    setMenuVisible(true);
  }, [gridRef]);

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const openDialog = () => {
    if (dialogRef.current) {
      dialogRef.current.open();
    }
  };

  const contextMenuOptions = useMemo(
    () => [
      { label: t("Duplicate"), action: duplicateStructures, icon: <FiCopy />, needSelectedItems: true },
      ...(isAdmin ? [{ label: t("Publish as Template"), action: publishStructures, icon: <FiShare2 />, needSelectedItems: true }] : []),
      { label: t("To Intra Company Database"), action: archiveStructures, icon: <FiShare2 />, needSelectedItems: true },
      { label: t("Delete"), action: openDialog, icon: <FiTrash2 />, needSelectedItems: true },
    ],
    [duplicateStructures, publishStructures, archiveStructures, openDialog, isAdmin, t]
  );

  // Focus and start editing new row smoothly after DOM settles
  useEffect(() => {
    if (!pendingFocusIdRef.current || !gridRef.current) return;
    const idToFocus = pendingFocusIdRef.current;
    pendingFocusIdRef.current = null;
    const timer = setTimeout(() => {
       const api = gridRef.current?.api;
       if (!api) {
         setIsAdding(false);
         return;
       }
       const node = api.getRowNode(idToFocus);
       if (node) {
         api.ensureIndexVisible(node.rowIndex, "bottom");
         api.setFocusedCell(node.rowIndex, "name");
         api.startEditingCell({ rowIndex: node.rowIndex, colKey: "name" });
       }
       setIsAdding(false);
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [structures]);

  // Load from workspace
  useEffect(() => {
    if (actionQueue.length > 0 || isSaveClicked) return;
    Structure.get_from_my_workspace()
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setStructures(data);
        }
      })
      .catch((err) => console.error("Error fetching structures:", err));
  }, [refresh]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  // Structure IDs for scope calculations (exclude unsaved to prevent focus-stealing background refetches)
  const structureIdsForScope = useMemo(() => {
    const unsavedIds = new Set(actionQueue.filter((a) => a.action === "new").map((a) => a.item.id));
    return structures.filter((s) => !unsavedIds.has(s.id) && s.id).map((s) => s.id);
  }, [structures, actionQueue]);

  const structureIdsKey = useMemo(() => structureIdsForScope.slice().sort().join(","), [structureIdsForScope]);

  // Fetch scope calculations
  useEffect(() => {
    if (!structureIdsKey) {
      setScopeData([]);
      return;
    }
    if (actionQueue.length > 0 || isSaveClicked) return;

    Structure.get_total_gwp_by_scope(structureIdsForScope)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setScopeData(data);
        }
      })
      .catch((err) => console.error("Error fetching scope data:", err));
  }, [structureIdsKey, refresh]);

  const formatEmissions = useCallback((valueInKg) => {
    const num = Number(valueInKg) || 0;
    if (num >= 1000) {
      const valInTons = num / 1000;
      return {
        value: valInTons.toLocaleString(undefined, { maximumFractionDigits: 2 }),
        unit: "t CO₂e",
      };
    }
    return {
      value: num.toLocaleString(undefined, { maximumFractionDigits: 2 }),
      unit: "kg CO₂e",
    };
  }, []);

  const handleExport = useCallback(() => {
    const worksheet = XLSX.utils.json_to_sheet(structures);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "organizations.xlsx");
  }, [structures]);

  return (
    <MainContainer>
      {/* ── KPI Summary Cards (Pinned to Top) ────────────────── */}
      <Box sx={{ mb: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <KpiGrid columns={4}>
          <KpiTile
            label={t("Gross emissions")}
            value={formatEmissions(totalGWP).value}
            unit={formatEmissions(totalGWP).unit}
            icon={<Co2OutlinedIcon />}
            tone="gwp"
            size="lg"
            footer={t("Scope 1 + 2 + 3")}
          />
          <KpiTile
            label={t("Scope 1")}
            value={formatEmissions(totalScope1).value}
            unit={formatEmissions(totalScope1).unit}
            icon={<FactoryOutlinedIcon />}
            tone="scope1"
            size="lg"
            footer={totalGWP > 0 ? `${pctScope1}% of gross` : "0% of gross"}
          />
          <KpiTile
            label={t("Scope 2")}
            value={formatEmissions(totalScope2).value}
            unit={formatEmissions(totalScope2).unit}
            icon={<BoltOutlinedIcon />}
            tone="scope2"
            size="lg"
            footer={totalGWP > 0 ? `${pctScope2}% of gross` : "0% of gross"}
          />
          <KpiTile
            label={t("Scope 3")}
            value={formatEmissions(totalScope3).value}
            unit={formatEmissions(totalScope3).unit}
            icon={<LocalShippingOutlinedIcon />}
            tone="scope3"
            size="lg"
            footer={totalGWP > 0 ? `${pctScope3}% of gross` : "0% of gross"}
          />
        </KpiGrid>
        <KpiGrid columns={3}>
          <KpiTile
            label={t("Avoided emissions")}
            value={formatEmissions(totalAvoided).value}
            unit={formatEmissions(totalAvoided).unit}
            icon={<EcoOutlinedIcon fontSize="small" />}
            tone="cost"
            footer={t("Not deducted from gross")}
          />
          <KpiTile
            label={t("Biogenic emissions")}
            value={formatEmissions(totalBiogenic).value}
            unit={formatEmissions(totalBiogenic).unit}
            icon={<ParkOutlinedIcon fontSize="small" />}
            tone="shield"
            footer={t("Reported outside the scopes")}
          />
          <KpiTile
            label={t("Emission records")}
            value={totalRecords}
            icon={<AssessmentOutlinedIcon fontSize="small" />}
            footer={recordsFooter}
          />
        </KpiGrid>
      </Box>

      {/* ── Dynamic Content Switching ──────────────────────────── */}
      <Box sx={{ minHeight: "600px", position: "relative" }}>
        {/* VIEW 1: Directory */}
        <DataTableCard
          title={`${t("Organization")} (${structures.length})`}
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              {/* Search Bar on Right */}
              <label
                className={m3Styles.search}
                style={{
                  height: "40px",
                  width: "280px",
                  maxWidth: "320px",
                  flex: "none",
                  margin: 0,
                }}
              >
                <SearchIcon fontSize="small" aria-hidden />
                <input
                  className={m3Styles.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder={t("Search Organization")}
                  aria-label={t("Search Organization")}
                />
                {searchText && (
                  <button
                    type="button"
                    className={m3Styles.searchClear}
                    onClick={() => setSearchText("")}
                    aria-label={t("Clear search")}
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </label>

              {/* Add Organization Button */}
              <M3Button
                variant="filled"
                icon={<FiPlus size={20} />}
                onClick={newOrganization}
                disabled={isAdding}
              >
                {t("Add Organization")}
              </M3Button>

              {/* Filter / Column Visibility */}
              <span ref={gridHeaderContainerRef} style={{ display: "inline-flex" }}>
                <M3IconButton
                  icon={<LuFilter size={20} />}
                  label={t("Column Visibility")}
                  onClick={() => setColumnMenuVisible((p) => !p)}
                  style={{
                    background: "var(--m3-primary-container)",
                    color: "var(--m3-on-primary-container)",
                  }}
                />
              </span>

              {/* Download / Export */}
              <M3IconButton
                icon={<RiDownload2Line size={20} />}
                label={t("Export as .xlsx")}
                onClick={handleExport}
                style={{
                  background: "var(--m3-primary-container)",
                  color: "var(--m3-on-primary-container)",
                }}
              />

              {/* Refresh */}
              <M3IconButton
                icon={<FiRefreshCw size={20} />}
                label={t("Refresh")}
                onClick={(e) => {
                  e.stopPropagation();
                  setRefresh((prev) => !prev);
                }}
                style={{
                  background: "var(--m3-primary-container)",
                  color: "var(--m3-on-primary-container)",
                }}
              />
            </Box>
          }
        >
          <AgGrid
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={structures}
            rowHeight={52}
            onSelectionChanged={onSelectionChanged}
            handleContextMenu={handleContextMenu}
            colVisibility_key={colVisibility_key}
            footerLeftContent={
              <GlobalViewToggle
                checked={globalView}
                onChange={setGlobalView}
                label={t("Global View")}
              />
            }
          />
        </DataTableCard>

        {/* VIEW 2: Global View Components */}
        <GlobalViewSection open={globalView}>
          <Box sx={{ pt: 3, pb: 2, animation: "org-fadeInUp 0.4s ease" }}>
            <OverallPerformanceAnalytics data={activeScopeData} organizations={targetStructures} />
          </Box>
        </GlobalViewSection>
      </Box>

      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={columnDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
      />
      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={derivedSelectedStructures} options={contextMenuOptions} />
      <SaveButton />
      <DialogDelete selectedItems={derivedSelectedStructures} handleDelete={deleteStructures} ref={dialogRef} />

      {/* M3 Custom Industry Modal */}
      <M3Dialog
        open={customIndustryDialogOpen}
        onClose={() => setCustomIndustryDialogOpen(false)}
        title={t("Custom Industry Type")}
        subtitle={t("Enter a custom industry or sector name for this organization")}
        maxWidth="xs"
        actions={
          <>
            <M3Button
              variant="text"
              onClick={() => setCustomIndustryDialogOpen(false)}
            >
              {t("Cancel")}
            </M3Button>
            <M3Button
              variant="filled"
              disabled={!customIndustryValue.trim()}
              onClick={() => {
                const customVal = customIndustryValue.trim();
                if (customVal && customIndustryRowId) {
                  const now = new Date().toString().split(" GMT")[0];
                  setStructures((prev) =>
                    prev.map((r) =>
                      r.id === customIndustryRowId ? { ...r, type: customVal, date_modified: now } : r
                    )
                  );
                  setSelectedStructures((prev) =>
                    prev.map((r) =>
                      r.id === customIndustryRowId ? { ...r, type: customVal, date_modified: now } : r
                    )
                  );
                  setActionQueue((q) => {
                    const isNew = q.some((a) => a.action === "new" && a.item.id === customIndustryRowId);
                    if (isNew) {
                      return q.map((a) =>
                        a.action === "new" && a.item.id === customIndustryRowId
                          ? { ...a, item: { ...a.item, type: customVal, date_modified: now } }
                          : a
                      );
                    }
                    const targetItem = structures.find((s) => s.id === customIndustryRowId);
                    return [...q, { action: "update", item: { ...targetItem, type: customVal, date_modified: now } }];
                  });
                  setIsSaveVisible(true);
                }
                setCustomIndustryDialogOpen(false);
              }}
            >
              {t("Save Industry")}
            </M3Button>
          </>
        }
      >
        <Box sx={{ mt: 1 }}>
          <M3Field
            fullWidth
            label={t("Industry Name")}
            placeholder={t("e.g. Space Tech, Telecom, Defense...")}
            value={customIndustryValue}
            onChange={setCustomIndustryValue}
            inputProps={{ autoFocus: true }}
          />
        </Box>
      </M3Dialog>
    </MainContainer>
  );
};
export default StructureDashboard;
